/**
 * BudgetAlertBanner Component
 *
 * Shows warning if any project/company is over budget
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState } from 'react';
import { Alert } from 'flowbite-react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../api/costs';

export interface BudgetAlert {
  id: string;
  type: 'project' | 'company' | 'client';
  name: string;
  totalCost: number;
  budgetCost: number;
  utilizationPct: number;
  overBudgetAmount: number;
}

export interface BudgetAlertBannerProps {
  alerts: BudgetAlert[];
  onDismiss?: (alertId: string) => void;
  linkToCostDashboard?: boolean;
}

export const BudgetAlertBanner: React.FC<BudgetAlertBannerProps> = ({
  alerts,
  onDismiss,
  linkToCostDashboard = true
}) => {
  const navigate = useNavigate();
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  if (alerts.length === 0) return null;

  // Filter out dismissed alerts
  const activeAlerts = alerts.filter(alert => !dismissedAlerts.has(alert.id));

  if (activeAlerts.length === 0) return null;

  const handleDismiss = (alertId: string) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
    if (onDismiss) {
      onDismiss(alertId);
    }
  };

  const handleViewCostDashboard = () => {
    if (linkToCostDashboard) {
      navigate('/costs');
    }
  };

  // Determine severity level
  const criticalAlerts = activeAlerts.filter(a => a.utilizationPct > 120);
  const warningAlerts = activeAlerts.filter(a => a.utilizationPct > 100 && a.utilizationPct <= 120);

  const severity = criticalAlerts.length > 0 ? 'critical' : 'warning';
  const color = severity === 'critical' ? 'failure' : 'warning';

  return (
    <div className="space-y-2">
      {/* Summary Banner */}
      <Alert
        color={color}
        icon={ExclamationTriangleIcon}
        className="border-l-4"
        additionalContent={
          <div className="mt-4 space-y-3">
            {/* Alert Details */}
            <div className="space-y-2">
              {activeAlerts.slice(0, 3).map(alert => (
                <div
                  key={alert.id}
                  className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        {alert.type}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {alert.name}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-700">
                      <span className="font-medium">{formatCurrency(alert.totalCost)}</span>
                      <span className="text-gray-500"> / {formatCurrency(alert.budgetCost)}</span>
                      <span className="ml-2 text-red-600 font-medium">
                        ({alert.utilizationPct.toFixed(0)}% utilized)
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Over budget</p>
                      <p className="text-sm font-bold text-red-600">
                        {formatCurrency(alert.overBudgetAmount)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDismiss(alert.id)}
                      className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                      aria-label={`Dismiss alert for ${alert.name}`}
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Show more indicator */}
            {activeAlerts.length > 3 && (
              <p className="text-sm text-gray-600">
                + {activeAlerts.length - 3} more {activeAlerts.length - 3 === 1 ? 'item' : 'items'} over budget
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              {linkToCostDashboard && (
                <button
                  onClick={handleViewCostDashboard}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Cost Dashboard
                </button>
              )}
              <button
                onClick={() => activeAlerts.forEach(alert => handleDismiss(alert.id))}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Dismiss All
              </button>
            </div>
          </div>
        }
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">
              Budget Alert: {activeAlerts.length} {activeAlerts.length === 1 ? 'Item' : 'Items'} Over Budget
            </h3>
            <p className="mt-1 text-sm">
              {criticalAlerts.length > 0 && (
                <span className="font-medium text-red-800">
                  {criticalAlerts.length} critical ({'>'}120%)
                </span>
              )}
              {criticalAlerts.length > 0 && warningAlerts.length > 0 && ', '}
              {warningAlerts.length > 0 && (
                <span className="font-medium text-yellow-800">
                  {warningAlerts.length} warning (100-120%)
                </span>
              )}
            </p>
          </div>
        </div>
      </Alert>

      {/* Individual Critical Alerts (optional, for high visibility) */}
      {criticalAlerts.length > 0 && criticalAlerts.length <= 2 && (
        <div className="space-y-2">
          {criticalAlerts.map(alert => (
            <Alert
              key={`critical-${alert.id}`}
              color="failure"
              className="border-l-4"
              onDismiss={() => handleDismiss(alert.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold">{alert.name}</span> is significantly over budget
                  <span className="ml-2 text-sm">
                    ({formatCurrency(alert.totalCost)} / {formatCurrency(alert.budgetCost)})
                  </span>
                </div>
              </div>
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default BudgetAlertBanner;
